import React, { forwardRef, useState, useImperativeHandle } from 'react';
import { Button as Btn } from 'antd';
import { Check } from '@nackle/origami-icons';
import { Spin } from '../spin/spin';
import classNames from 'classnames';
/**
 * A custom Button component that extends Ant Design's Button.
 * It allows for an optional right-aligned icon.
 * 
 * {@link https://ant.design/components/button#api Ant Design Button}
 *
 * @module SuccessButton
 * @param {Object} props - The component props.
 * @param {React.ReactNode} [props.icon] - Icon to display on the button.
 * @param {React.ReactNode} [props.rightIcon] - Icon to display on the right side of the button.
 * @param {React.ReactNode} [props.successText] - Text to display in button when showSuccess is called.
 * @param {React.ReactNode} [props.loadingText] - Text to display in button while loading=true.
 * @param {React.string} [props.className] - custom class name for the button
 * @param {React.ReactNode} props.children - The content of the button.
 * @param {React.Ref} ref - The ref to be forwarded to the button element.
 * @returns {React.ReactElement} The rendered Button component.
 */

const SuccessButton = forwardRef( ( props, ref ) => {
    const [ success, setSuccess ] = useState( false );
    const { rightIcon, children, icon, successText, loadingText, className, ...restProps } = props;

    useImperativeHandle( ref, () => {
       return {
           showSuccess() {
               setSuccess( true );
               setTimeout( () =>{
                   setSuccess( false );
               }, 1000 );
           }
       };

      }, [ props.loading, success ] );
    const buttonClasses = classNames( {
        success: success ? true : false,
        [ className ]: className ? true : false
    } );

    return rightIcon ? (
        <Btn icon={ success ? <Check /> : icon } loading={ props.loading ? { icon: <Spin className="button-spin" size="mini" /> } : false } ref={ ref } { ...restProps }  className={ buttonClasses }>
            { success && successText ? successText : props.loading && loadingText ? loadingText : children }
            <span className='ant-btn-icon ant-btn-icon-right'>{ rightIcon }</span>
        </Btn>
    ) : (
        <Btn icon={ success ? <Check /> : icon } loading={ props.loading ? { icon: <Spin className="button-spin" size="mini" /> } : false } ref={ ref } { ...restProps } className={ buttonClasses }>
            { success && successText ? successText : props.loading && loadingText ? loadingText : children }
        </Btn>
    );
} );

export { SuccessButton };
